import { useState } from "react";
import { RotateCcwIcon, SparklesIcon } from "lucide-react";
import type { CmmdProjectContentSurface } from "@cmmd-forge/contracts";

import { createCmmdApiClient } from "~/environments/cmmdApi";

const cmmdApiClient = createCmmdApiClient();

export interface ProjectContentReadabilityControlsProps {
  readonly projectId: string | number;
  readonly surface: CmmdProjectContentSurface;
  readonly value: string;
  readonly disabled?: boolean;
  readonly onChange: (value: string) => void;
  /** inline sits in a composer footer without the helper caption. */
  readonly layout?: "default" | "inline";
}

/**
 * Shared, explicit AI formatting control for canonical project Markdown.
 * The returned content only replaces the local draft; callers still own Save.
 */
export function ProjectContentReadabilityControls(props: ProjectContentReadabilityControlsProps) {
  const [formatting, setFormatting] = useState(false);
  const [undoValue, setUndoValue] = useState<string | null>(null);
  const [formattedValue, setFormattedValue] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canUndo = undoValue !== null && formattedValue === props.value;

  const improve = async () => {
    if (!props.value.trim()) return;
    const source = props.value;
    setFormatting(true);
    setError(null);
    try {
      const result = await cmmdApiClient.improveProjectContent(String(props.projectId), {
        surface: props.surface,
        content: source,
      });
      setUndoValue(source);
      setFormattedValue(result.content);
      props.onChange(result.content);
    } catch {
      setError("Couldn’t improve this draft. Nothing was changed—try again.");
    } finally {
      setFormatting(false);
    }
  };

  const undo = () => {
    if (undoValue === null) return;
    props.onChange(undoValue);
    setUndoValue(null);
    setFormattedValue(null);
    setError(null);
  };

  const inline = props.layout === "inline";

  return (
    <div
      className={
        inline ? "flex flex-wrap items-center gap-1.5" : "mt-2 flex flex-wrap items-center gap-2"
      }
    >
      <button
        type="button"
        onClick={() => void improve()}
        disabled={props.disabled === true || formatting || !props.value.trim()}
        title="Draft only · uses project AI allowance. Updates this draft without saving it."
        className={
          inline
            ? "inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground disabled:opacity-40"
            : "inline-flex h-7 items-center gap-1.5 rounded-md border border-border/60 px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground disabled:opacity-40"
        }
      >
        <SparklesIcon className="size-3.5" aria-hidden />
        {formatting ? "Improving…" : "Improve readability"}
      </button>
      {canUndo ? (
        <button
          type="button"
          onClick={undo}
          disabled={props.disabled === true || formatting}
          className="inline-flex h-7 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground disabled:opacity-40"
        >
          <RotateCcwIcon className="size-3.5" aria-hidden />
          Undo AI format
        </button>
      ) : null}
      {inline ? null : (
        <span className="text-[11px] text-muted-foreground/70">
          Draft only · uses workspace AI allowance
        </span>
      )}
      {error ? (
        <p role="alert" className="basis-full text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
